import { Action, action, Thunk, thunk } from 'easy-peasy';
import { OrderedMap } from 'immutable';
import {
  DashboardProductRequest,
  DashboardProductResponse, PriceCalculationResponse,
  Product,
  ProductVpDetail, RecalculationStrategy,
} from '../api/types';
import { StoreModel } from './types';
import { productsVpApi } from '../api';

export const defaultProduct: ProductVpDetail = {
  actualPrice: 0,
  actualPriceDiscount: 0,
  purchasePrice: 0,
  purchasePriceCurrency: '',
  sellingPriceCurrency: '',
  basePrice: 0,
  loyaltyBasePrice: 0,
  loyaltyBasePriceVp: 0,
  newPrice: 0,
  newPriceDiscount: 0,
  productDescription: '',
  id: -1,
  productId: '',
  supplier: { id: '', name: '' },
  category: { id: '', name: '' },
  parameters: [],
  ean: '',
  skipTopN: false,
  skipMinMargin: false,
  skipMaxDiscount: false,
  rrp: 0,
  rsp: 0,
  sellingPriceRate: 0,
  rounding: 0,
  change: 0,
  pricingError: false,
  rules: [],
};

export interface DashboardProductsVpStore {
  isLoading: boolean;
  list: OrderedMap<string, Product>;
  pagination: {
    total: number;
  };
  detail: ProductVpDetail;
  calculationState: PriceCalculationResponse;
  selectedDetail?: { id: number };
  lastFetchListRequest?: DashboardProductRequest;
  setSelectedDetail: Action<DashboardProductsVpStore, { id: number }>;
  setFetchListRequest: Action<DashboardProductsVpStore, DashboardProductRequest>;
  setLoading: Action<DashboardProductsVpStore, boolean>;
  setList: Action<DashboardProductsVpStore, DashboardProductResponse>;
  setDetail: Action<DashboardProductsVpStore, ProductVpDetail>;
  setCalculationState: Action<DashboardProductsVpStore, PriceCalculationResponse>;
  recalculatePrices: Thunk<DashboardProductsVpStore, RecalculationStrategy, any, StoreModel>;
  confirmAllDraftRules: Thunk<DashboardProductsVpStore, void, any, StoreModel>;
  fetchList: Thunk<DashboardProductsVpStore,
    DashboardProductRequest,
    any,
    StoreModel>;
  fetchListByLastRequest: Thunk<DashboardProductsVpStore, void, any, StoreModel>;
  fetchProductDetail: Thunk<DashboardProductsVpStore,
    { id: number },
    any,
    StoreModel>;
  fetchCalculationState: Thunk<DashboardProductsVpStore, void, any, StoreModel>
}

const dashboardProductsVp: DashboardProductsVpStore = {
  isLoading: false,
  list: OrderedMap(),
  pagination: {
    total: 0,
  },
  detail: defaultProduct,
  calculationState: { running: false, lastRunOutcome: 'OK' },
  selectedDetail: undefined,
  setSelectedDetail: action((state, payload) => {
    state.selectedDetail = payload;
  }),
  setLoading: action((state, payload) => {
    state.isLoading = payload;
  }),
  setList: action((state, { data, page }) => {
    state.list = OrderedMap(data.map(v => [`${v.id}`, v]));
    state.pagination.total = page.total;
  }),
  setDetail: action((state, payload) => {
    state.detail = payload;
  }),
  setCalculationState: action((state, payload) => {
    state.calculationState = payload;
  }),
  setFetchListRequest: action((state, payload) => {
    state.lastFetchListRequest = payload;
  }),
  fetchList: thunk(async (actions, request) => {
    try {
      actions.setLoading(true);
      const { data } = await productsVpApi.getProducts(request);

      actions.setList(data);
      actions.setLoading(false);
      actions.setFetchListRequest(request);
    } catch (error) {
      actions.setLoading(false);
    }
  }),
  fetchListByLastRequest: thunk(async (actions, payload, { getState, getStoreActions }) => {
    const request = getState().lastFetchListRequest;
    if (request) {
      getStoreActions().dashboardProductsVp.fetchList(request);
    }
  }),
  fetchProductDetail: thunk(async (actions, value) => {
    try {
      const { data } = await productsVpApi.getProductDetail(value.id);
      actions.setDetail(data);
    } catch (error) {
      return false;
    }
    return true;
  }),
  fetchCalculationState: thunk(async actions => {
    const { data } = await productsVpApi.getCalculationState();
    actions.setCalculationState(data);
  }),
  recalculatePrices: thunk(async (actions, payload) => {
    const { data } = await productsVpApi.recalculatePrices(payload);
    actions.setCalculationState(data);
  }),
  confirmAllDraftRules: thunk(async actions => {
    const { data } = await productsVpApi.confirmAllDraftRules();
    actions.setCalculationState(data);
  }),
};

export default dashboardProductsVp;
